/**
 * The chat panel's half of the workflow runner: find or create each lane's
 * CaseBlink Workflow thread, send one fixed prompt there as an Agent turn,
 * and resolve when that turn has ended. The main lane's thread opens in the
 * chat and is followed from it (its status, the thread's `active_turn`, an
 * open approval or question). The other lanes run side by side without
 * taking the chat: their prompt is posted to the thread's chat endpoint and
 * the thread is polled until its turn ends.
 */
import type { UIMessage } from "ai";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { getAuthToken } from "@/api/axiosConfig";
import {
  AgentAPI,
  agentChatUrl,
  type AgentThread
} from "@/features/agents/api/agentApi";
import { AUTO_MODEL_ID } from "@/features/agents/types";
import {
  transcriptWaiting,
  type AgentChat
} from "@/features/agents/useAgentChat";

import { WORKFLOW_LANE_TITLES } from "../catalog";
import { useWorkflowsOptional } from "../context/WorkflowsContext";
import { busyHold, holdOf, transcriptHold, type WorkflowHold } from "../holds";
import { liveDetail, readTurn } from "../questions";
import { readThreadId, writeThreadId } from "../storage";
import type {
  WorkflowExecutor,
  WorkflowLane,
  WorkflowTurnOutcome,
  WorkflowTurnPhase,
  WorkflowTurnRequest
} from "../types";

const POLL_MS = 400;
/** A lane the chat doesn't show is read from the server, less often. */
const REMOTE_POLL_MS = 1500;
/** Polls in a row with no turn and no pause before a step counts as ended. */
const SETTLED_POLLS = 3;
const REMOTE_SETTLED_POLLS = 2;
const START_TIMEOUT_MS = 90_000;
const IDLE_TIMEOUT_MS = 10 * 60_000;
/** How often an open thread that looks busy or held is re-read from the server. */
const RECHECK_MS = 10_000;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const byUpdated = (a: AgentThread, b: AgentThread) =>
  (b.updated_at ?? "").localeCompare(a.updated_at ?? "");

const now = (messages: UIMessage[], sent: string) =>
  liveDetail(messages, sent) ?? undefined;

const failed = (detail: string, threadId?: string): WorkflowTurnOutcome => ({
  status: "failed",
  detail,
  ...(threadId ? { threadId } : {})
});

/** The turn's reading as an outcome. */
const outcomeOf = (
  messages: UIMessage[],
  sent: string,
  threadId: string
): WorkflowTurnOutcome => {
  const r = readTurn(messages, sent);
  if (r.failed) return failed(r.failed, threadId);
  if (r.stopped) return failed("Stopped", threadId);
  return {
    status: "done",
    threadId,
    ...(r.model ? { model: r.model } : {}),
    ...(r.questions.length ? { questions: r.questions } : {})
  };
};

/** The step's prompt, if this thread already has it. */
const lastSentId = (
  messages: UIMessage[],
  workflowId: string
): string | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    const id = (message.metadata as { workflow?: { id?: string } } | undefined)
      ?.workflow?.id;
    if (message.role === "user" && id === workflowId) return message.id;
  }
  return null;
};

type Remote = {
  busy: boolean;
  paused: boolean;
  queued: boolean;
  messages: UIMessage[];
  /** What the paused thread waits on; `own` when the step's turn raised it. */
  hold: WorkflowHold | null;
};

type OnPhase = (
  phase: WorkflowTurnPhase,
  threadId?: string,
  hold?: WorkflowHold,
  detail?: string
) => void;

const pauseOf = (
  thread: Pick<AgentThread, "id" | "pending_question" | "pending_approval">,
  messages: UIMessage[],
  sent: string | null
): WorkflowHold | null =>
  holdOf(thread, messages, sent) ??
  (transcriptWaiting(messages)
    ? transcriptHold(thread.id, messages, sent)
    : null);

/** "Casey's question "Which passport…" (from Case Insights)". */
const holdName = (hold: WorkflowHold) =>
  `${
    hold.kind === "approval"
      ? "A change waiting for approval"
      : "Casey's question"
  }${hold.summary && hold.kind === "question" ? ` "${hold.summary}"` : ""}${
    hold.source ? ` (from ${hold.source})` : ""
  }`;

export const useWorkflowExecutor = (chat: AgentChat, individualId: string) => {
  const register = useWorkflowsOptional()?.registerExecutor;
  const chatRef = useRef(chat);
  chatRef.current = chat;

  useEffect(() => {
    if (!register) return undefined;
    const threadOf = (id: string) =>
      chatRef.current.threads.find((t) => t.id === id) ?? null;
    const open = (id: string) => chatRef.current.threadId === id;
    const busy = (id: string) => {
      const c = chatRef.current;
      if (open(id))
        return (
          c.status === "submitted" ||
          c.status === "streaming" ||
          c.activeTurn !== null
        );
      return Boolean(threadOf(id)?.active_turn);
    };
    // Messages people queued on the thread go before a step that comes later.
    const queuedAhead = (id: string) => {
      const c = chatRef.current;
      if (open(id)) return !c.queue.paused && c.queue.items.length > 0;
      return (threadOf(id)?.queued_messages?.length ?? 0) > 0;
    };
    const checkedAt = new Map<string, number>();
    /**
     * The open thread's lock and pending question reach the chat over the
     * realtime stream, which a hidden tab doesn't hold. While the chat shows
     * either with no stream of its own, re-read the threads now and then so
     * a turn that ended unseen doesn't hold the step and the queue behind it.
     */
    const recheck = (id: string) => {
      const c = chatRef.current;
      const t = threadOf(id);
      if (c.status === "submitted" || c.status === "streaming") return;
      if (c.activeTurn === null && !t?.pending_question && !t?.pending_approval)
        return;
      if (Date.now() - (checkedAt.get(id) ?? 0) < RECHECK_MS) return;
      checkedAt.set(id, Date.now());
      void c.refreshThreads().catch(() => undefined);
    };
    /**
     * A lane thread as the chat has it when it's open, else as the server
     * does. `sent` is the step's prompt once sent, to tell its own pause
     * from one another turn raised.
     */
    const remote = async (
      id: string,
      sent: string | null = null
    ): Promise<Remote> => {
      if (open(id)) {
        recheck(id);
        const { messages } = chatRef.current;
        const hold = pauseOf(
          threadOf(id) ?? {
            id,
            pending_question: null,
            pending_approval: null
          },
          messages,
          sent
        );
        return {
          busy: busy(id),
          paused: hold !== null,
          queued: queuedAhead(id),
          messages,
          hold
        };
      }
      const { thread, messages } = await AgentAPI.getThread(
        chatRef.current.scope,
        id
      );
      const hold = pauseOf(thread, messages, sent);
      return {
        busy: Boolean(thread.active_turn),
        paused: hold !== null,
        queued: (thread.queued_messages?.length ?? 0) > 0,
        messages,
        hold
      };
    };
    /** A new thread for the lane, so a question another turn left stays as it is. */
    const freshThread = async (lane: WorkflowLane): Promise<string | null> => {
      try {
        const created =
          lane === "main"
            ? await chatRef.current.newThread()
            : await AgentAPI.createThread(chatRef.current.scope, {
                title: WORKFLOW_LANE_TITLES[lane]
              });
        writeThreadId(individualId, created.id, lane);
        if (lane !== "main") await chatRef.current.refreshThreads();
        return created.id;
      } catch {
        return null;
      }
    };
    /**
     * Wait until the lane's thread is free to take the step's prompt. A turn
     * already on it (a teammate, a background notice) or a message queued
     * behind it goes first. A question or approval another turn left would
     * be skipped by the next message sent there, so the step moves to a new
     * thread for its lane once; if it can't, it waits for the answer. Until
     * the prompt is sent the step is only queued: an abort cancels it.
     */
    const awaitFree = async (
      lane: WorkflowLane,
      start: string,
      onPhase: OnPhase,
      signal: AbortSignal,
      pollMs: number
    ): Promise<string | WorkflowTurnOutcome> => {
      let id = start;
      let moved = false;
      const idleBy = Date.now() + IDLE_TIMEOUT_MS;
      for (;;) {
        if (signal.aborted) return { status: "cancelled" };
        // eslint-disable-next-line no-await-in-loop
        const r = await remote(id).catch(() => null);
        if (r && !r.busy && !r.paused && !r.queued) return id;
        if (r?.busy) onPhase("waiting", id, busyHold(id, r.messages));
        else if (r?.hold && !moved) {
          moved = true;
          const held = r.hold;
          // eslint-disable-next-line no-await-in-loop
          const next = await freshThread(lane);
          if (next) {
            const from = id;
            toast.info(
              `${holdName(
                held
              )} stays open in the earlier workflow thread. The run goes on in a new one.`,
              {
                action: {
                  label:
                    held.kind === "approval"
                      ? "Open Approval"
                      : "Open Question",
                  onClick: () => chatRef.current.setThreadId(from)
                }
              }
            );
            id = next;
            // eslint-disable-next-line no-continue
            continue;
          }
          onPhase("waiting", id, held);
        } else if (r?.hold) onPhase("waiting", id, r.hold);
        else onPhase("waiting", id);
        if (Date.now() > idleBy)
          return failed("The workflow thread stayed busy", id);
        // eslint-disable-next-line no-await-in-loop
        await sleep(pollMs);
      }
    };
    const find = (lane: WorkflowLane): AgentThread | null => {
      const threads = chatRef.current.threads.filter((t) => !t.archived);
      const stored = readThreadId(individualId, lane);
      return (
        threads.find((t) => t.id === stored) ??
        threads
          .filter((t) => t.title === WORKFLOW_LANE_TITLES[lane])
          .sort(byUpdated)[0] ??
        null
      );
    };
    // The server names a thread after its first message; put the name back.
    const keepName = (id: string, lane: WorkflowLane) => {
      const t = threadOf(id);
      if (!t) return;
      if (t.title !== WORKFLOW_LANE_TITLES[lane])
        void chatRef.current
          .renameThread(id, WORKFLOW_LANE_TITLES[lane])
          .catch(() => undefined);
      if (!t.pinned)
        void chatRef.current.setPinned(id, true).catch(() => undefined);
    };
    const ensureThread = async (lane: WorkflowLane): Promise<string> => {
      for (let i = 0; chatRef.current.threadsLoading && i < 50; i += 1)
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      const found = find(lane);
      if (found) {
        writeThreadId(individualId, found.id, lane);
        return found.id;
      }
      // The list can still be the one from before this lane's thread existed.
      await chatRef.current.refreshThreads().catch(() => undefined);
      const again = find(lane);
      if (again) {
        writeThreadId(individualId, again.id, lane);
        return again.id;
      }
      // A stored thread the list missed still has the run. Open that one.
      // A new empty thread would look like the chat was wiped.
      const stored = readThreadId(individualId, lane);
      if (stored) {
        try {
          const { thread } = await AgentAPI.getThread(
            chatRef.current.scope,
            stored
          );
          if (!thread.archived) {
            writeThreadId(individualId, thread.id, lane);
            await chatRef.current.refreshThreads().catch(() => undefined);
            return thread.id;
          }
        } catch {
          // The stored thread is gone; open a new one below.
        }
      }
      // The main lane's thread opens in the chat; the others stay out of it.
      const created =
        lane === "main"
          ? await chatRef.current.newThread()
          : await AgentAPI.createThread(chatRef.current.scope, {
              title: WORKFLOW_LANE_TITLES[lane]
            });
      writeThreadId(individualId, created.id, lane);
      if (lane !== "main") await chatRef.current.refreshThreads();
      return created.id;
    };

    /** The main lane: the step runs in the open chat. */
    const runOpen = async (
      request: WorkflowTurnRequest,
      onPhase: OnPhase,
      signal: AbortSignal
    ): Promise<WorkflowTurnOutcome> => {
      const first = await ensureThread("main");
      // Every step shows in the chat beside the cards, even while it waits its turn.
      if (!open(first)) chatRef.current.setThreadId(first);
      const free = await awaitFree("main", first, onPhase, signal, POLL_MS);
      if (typeof free !== "string") return free;
      const id = free;
      if (!open(id)) chatRef.current.setThreadId(id);

      let messageId: string | null = null;
      for (let i = 0; !messageId && i < 20; i += 1) {
        if (signal.aborted) return { status: "cancelled" };
        messageId = chatRef.current.sendOnThread(id, request.text, {
          skillId: request.skillId,
          metadata: { workflow: request.stamp }
        });
        // eslint-disable-next-line no-await-in-loop
        if (!messageId) await sleep(POLL_MS * 2);
      }
      if (!messageId) return failed("Couldn't send the prompt", id);
      onPhase("running", id);

      const startBy = Date.now() + START_TIMEOUT_MS;
      const sent = messageId;
      const started = () =>
        busy(id) ||
        (open(id) && chatRef.current.messages.some((m) => m.id === sent));
      while (!started()) {
        if (signal.aborted) return failed("Stopped", id);
        if (open(id) && chatRef.current.status === "error")
          return failed(chatRef.current.error ?? "The turn failed", id);
        if (Date.now() > startBy) return failed("Casey didn't start", id);
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
      }
      keepName(id, "main");

      let settled = 0;
      while (settled < SETTLED_POLLS) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(open(id) ? POLL_MS : REMOTE_POLL_MS);
        if (signal.aborted) {
          if (open(id) && busy(id)) void chatRef.current.stopTurn();
          else if (busy(id))
            void AgentAPI.stopThread(chatRef.current.scope, id).catch(
              () => undefined
            );
          return failed("Stopped", id);
        }
        // eslint-disable-next-line no-await-in-loop
        const r = await remote(id, sent).catch(() => null);
        if (r) {
          // A pause another turn raised after this one ended isn't this step's.
          const own = !r.busy && r.hold?.own ? r.hold : null;
          if (own) onPhase("needs-you", id, own);
          else onPhase("running", id, undefined, now(r.messages, sent));
          settled = r.busy || own ? 0 : settled + 1;
        }
      }
      keepName(id, "main");

      if (!open(id)) {
        try {
          const { messages } = await AgentAPI.getThread(
            chatRef.current.scope,
            id
          );
          return outcomeOf(messages, sent, id);
        } catch {
          return { status: "done", threadId: id };
        }
      }
      const c = chatRef.current;
      if (c.status === "error") return failed(c.error ?? "The turn failed", id);
      return outcomeOf(c.messages, sent, id);
    };

    /** Post the prompt to a thread the chat doesn't show; the turn runs on without us. */
    const post = async (
      id: string,
      request: WorkflowTurnRequest,
      signal: AbortSignal
    ): Promise<string> => {
      const messageId = `user_${crypto.randomUUID().replace(/-/g, "")}`;
      const t = threadOf(id);
      const token = await getAuthToken();
      const res = await fetch(agentChatUrl(chatRef.current.scope, id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          id,
          trigger: "submit-message",
          messageId,
          messages: [
            {
              id: messageId,
              role: "user",
              parts: [{ type: "text", text: request.text }],
              metadata: {
                workflow: request.stamp,
                audience: "agent",
                mentions: []
              }
            }
          ],
          mode: "agent",
          skill_id: request.skillId ?? "",
          model: t?.model ?? AUTO_MODEL_ID,
          thinking: t?.thinking ?? false,
          ...(t?.optimize_for ? { optimize_for: t.optimize_for } : {})
        }),
        signal
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(detail.trim().slice(0, 200) || `HTTP ${res.status}`);
      }
      // Read the stream to its end so the connection closes cleanly; the
      // thread's state, not the stream, says how the turn went.
      void (async () => {
        const reader = res.body?.getReader();
        if (!reader) return;
        try {
          for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { done } = await reader.read();
            if (done) break;
          }
        } catch {
          // Aborted or dropped; the turn goes on on the server.
        }
      })();
      return messageId;
    };

    /** A side lane: the step runs on its own thread without taking the chat. */
    const runRemote = async (
      request: WorkflowTurnRequest,
      onPhase: OnPhase,
      signal: AbortSignal
    ): Promise<WorkflowTurnOutcome> => {
      const { lane } = request;
      const free = await awaitFree(
        lane,
        await ensureThread(lane),
        onPhase,
        signal,
        REMOTE_POLL_MS
      );
      if (typeof free !== "string") return free;
      const id = free;

      const stream = new AbortController();
      let sent: string;
      try {
        sent = open(id)
          ? chatRef.current.sendOnThread(id, request.text, {
              skillId: request.skillId,
              metadata: { workflow: request.stamp }
            }) ?? ""
          : await post(id, request, stream.signal);
      } catch (e) {
        return failed(
          e instanceof Error ? e.message : "Couldn't send the prompt",
          id
        );
      }
      if (!sent) return failed("Couldn't send the prompt", id);
      onPhase("running", id);
      const stop = () => {
        stream.abort();
        void AgentAPI.stopThread(chatRef.current.scope, id).catch(
          () => undefined
        );
      };

      try {
        const startBy = Date.now() + START_TIMEOUT_MS;
        for (;;) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(REMOTE_POLL_MS);
          if (signal.aborted) {
            stop();
            return failed("Stopped", id);
          }
          // eslint-disable-next-line no-await-in-loop
          const r = await remote(id).catch(() => null);
          if (r && (r.busy || r.messages.some((m) => m.id === sent))) break;
          if (Date.now() > startBy) return failed("Casey didn't start", id);
        }
        keepName(id, lane);

        let settled = 0;
        let last: Remote | null = null;
        while (settled < REMOTE_SETTLED_POLLS) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(REMOTE_POLL_MS);
          if (signal.aborted) {
            stop();
            return failed("Stopped", id);
          }
          // eslint-disable-next-line no-await-in-loop
          const r = await remote(id, sent).catch(() => null);
          if (r) {
            last = r;
            const own = !r.busy && r.hold?.own ? r.hold : null;
            if (own) onPhase("needs-you", id, own);
            else onPhase("running", id, undefined, now(r.messages, sent));
            settled = r.busy || own ? 0 : settled + 1;
          }
        }
        keepName(id, lane);
        return outcomeOf(last?.messages ?? [], sent, id);
      } finally {
        stream.abort();
      }
    };

    const readServer = async (id: string) => {
      try {
        return await AgentAPI.getThread(chatRef.current.scope, id);
      } catch {
        return null;
      }
    };
    /**
     * The prompt is already on the lane's thread (a refresh, or a missed
     * end). Wait for that turn and resolve. Never send it a second time,
     * and never open a new thread over the one that has the run.
     */
    const resume = async (
      request: WorkflowTurnRequest,
      threadId: string | undefined,
      onPhase: OnPhase,
      signal: AbortSignal
    ): Promise<WorkflowTurnOutcome> => {
      const { lane } = request;
      const send =
        lane === "main"
          ? () => runOpen(request, onPhase, signal)
          : () => runRemote(request, onPhase, signal);
      const id = threadId || readThreadId(individualId, lane) || "";
      if (!id) return send();
      const first = await readServer(id);
      if (!first || first.thread.archived) return send();
      const sent = lastSentId(first.messages, request.workflowId);
      // Show the thread the run is already on so its messages stay put.
      if (lane === "main" && !open(id)) chatRef.current.setThreadId(id);
      if (!sent) return send();
      const picture = (messages: UIMessage[], thread: AgentThread) => {
        const hold = pauseOf(thread, messages, sent);
        return { hold, busy: Boolean(thread.active_turn) };
      };
      const firstState = picture(first.messages, first.thread);
      if (!firstState.busy && !firstState.hold?.own)
        return outcomeOf(first.messages, sent, id);
      onPhase(
        firstState.hold?.own ? "needs-you" : "running",
        id,
        firstState.hold?.own ? firstState.hold : undefined,
        firstState.hold?.own ? undefined : now(first.messages, sent)
      );
      let settled = 0;
      let misses = 0;
      let lastMessages = first.messages;
      while (settled < REMOTE_SETTLED_POLLS) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(REMOTE_POLL_MS);
        if (signal.aborted) {
          void AgentAPI.stopThread(chatRef.current.scope, id).catch(
            () => undefined
          );
          return failed("Stopped", id);
        }
        // eslint-disable-next-line no-await-in-loop
        const snap = await readServer(id);
        if (!snap) {
          misses += 1;
          if (misses >= 5)
            return failed("Couldn't read the workflow thread", id);
        } else {
          misses = 0;
          lastMessages = snap.messages;
          const state = picture(snap.messages, snap.thread);
          if (state.hold?.own && !state.busy)
            onPhase("needs-you", id, state.hold);
          else onPhase("running", id, undefined, now(snap.messages, sent));
          settled = state.busy || state.hold?.own ? 0 : settled + 1;
        }
      }
      keepName(id, lane);
      return outcomeOf(lastMessages, sent, id);
    };

    const executor: WorkflowExecutor = {
      run: (request, onPhase, signal) =>
        request.lane === "main"
          ? runOpen(request, onPhase, signal)
          : runRemote(request, onPhase, signal),
      resume,
      open: (id) => chatRef.current.setThreadId(id)
    };
    return register(executor);
  }, [register, individualId]);
};
